import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Typography, Paper } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const scoreToQualitativeMap = {
  1: "5 - Bastante alejado del esperado",
  2: "4 - Desempeño por debajo del esperado",
  3: "3 - Desempeño esperado",
  4: "2 - Desempeño por encima del esperado",
  5: "1 - Excede ampliamente las expectativas",
};

const shortScoreLabels = {
  1: "Bastante Alejado",
  2: "Por Debajo",
  3: "Esperado",
  4: "Por Encima",
  5: "Excede Expectativas",
};

export default function GaussianBellCurve({ distribution }) {
  const scoreCounts = useMemo(() => {
    return Object.keys(distribution).reduce((acc, key) => {
      acc[parseInt(key)] = distribution[key];
      return acc;
    }, {});
  }, [distribution]);

  const labels = useMemo(() => Object.keys(scoreToQualitativeMap).sort((a, b) => parseInt(a) - parseInt(b)), []);

  const totalScores = useMemo(() => {
    return Object.values(scoreCounts).reduce((sum, count) => sum + count, 0);
  }, [scoreCounts]);

  const data = useMemo(() => {
    const datasetData = labels.map(label => totalScores > 0 ? (scoreCounts[parseInt(label)] || 0) / totalScores : 0);

    return {
      labels: labels.map(label => label),
      datasets: [
        {
          label: 'Distribución de Puntaje Calibrado',
          data: datasetData,
          backgroundColor: 'rgba(53, 162, 235, 0.2)',
          borderColor: 'rgba(53, 162, 235, 1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    };
  }, [labels, scoreCounts, totalScores]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Distribución Normal de Puntajes Calibrados',
        font: { size: 18, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const numericScore = Object.keys(scoreToQualitativeMap).find(key => shortScoreLabels[key] === context.label);
            const fullScoreLabel = scoreToQualitativeMap[parseInt(numericScore)];
            const rawCount = scoreCounts[parseInt(numericScore)] || 0;
            return `${label}: ${fullScoreLabel} (Total: ${rawCount})`;
          }
        },
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Puntaje',
          font: { size: 14, weight: 'bold' },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Proporción de Evaluaciones',
          font: { size: 14, weight: 'bold' },
        },
        beginAtZero: true,
      },
    },
  }), [scoreCounts, labels]);

  if (!distribution || Object.keys(distribution).length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="subtitle1">No hay datos de distribución de puntaje disponibles para el período seleccionado.</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: '12px' }}>
      <Box sx={{ height: '500px', width: '100%' }}>
        <Line data={data} options={options} />
      </Box>
    </Paper>
  );
} 